import React, { useState } from 'react';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import { STATE_LABELS } from './kmLabels.js';
import { KM_CONFIG } from '../../api/config.js';

/* ═══════════════════════════════════════════════════════════
   Transizioni manuali di stato risorsa — riscritto sul contratto reale
   (Collaudo_KM_TalentGPT_20260907_2.docx, Appendice E.3/E.4, verificato
   dal vivo il 18/09).

   PATCH /resources/{id}/state si aspetta { nuovo_stato, stato_atteso } —
   non { to_state } come ipotizzato prima. stato_atteso è un controllo di
   concorrenza: se qualcun altro ha già spostato la risorsa, il server
   risponde 409 e non scrive nulla. Regola esplicita del collaudo (E.4):
   "L'interfaccia ricarica lo stato e non applica la transizione" — quindi
   sul 409 si ricarica, non si mostra solo un errore statico.
═══════════════════════════════════════════════════════════ */

function allowedTransitions(currentState) {
  if (currentState === 'R2') return ['R3', 'R4'];
  if (currentState && currentState.startsWith('R3')) return ['R4'];
  return [];
}

const TRANSITION_LABELS = {
  R3: 'Segna come Proponibile',
  R4: 'Segna per cancellazione',
};

function buildStateTransitionPayload(nuovoStato, statoAtteso) {
  return { nuovo_stato: nuovoStato, stato_atteso: statoAtteso };
}

export default function ResourceStateManager({ resourceId, onStateChanged }) {
  const kmClient = useRealKmClient();

  const [status, setStatus] = useState('idle'); // idle | loading | ready | not_tracked | error
  const [currentState, setCurrentState] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const [pending, setPending] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadState = React.useCallback(() => {
    if (!resourceId) return;
    setStatus('loading');
    setErrorMessage(null);
    kmClient
      .get(`/resources/${encodeURIComponent(resourceId)}`)
      .then((data) => {
        // stato_ciclo_vita è alla radice della risposta, non annidato.
        setCurrentState(data?.stato_ciclo_vita ?? null);
        setStatus('ready');
      })
      .catch((err) => {
        if (err?.status === 404) {
          // Non un errore: la maggior parte delle persone in MultiFitting
          // oggi non ha ancora un candidato KM collegato (gap Altamira↔KM,
          // trovato il 23/09). Distinto dall'errore vero apposta, per non
          // mostrare un messaggio grezzo di backend come se fosse un guasto.
          setStatus('not_tracked');
        } else {
          setErrorMessage(err?.detail || 'Errore nel caricamento dello stato.');
          setStatus('error');
        }
      });
  }, [kmClient, resourceId]);

  React.useEffect(() => {
    setResult(null);
    loadState();
  }, [loadState]);

  async function handleConfirm(toState) {
    setSending(true);
    setResult(null);
    try {
      const updated = await kmClient.patch(
        `/resources/${encodeURIComponent(resourceId)}/state`,
        buildStateTransitionPayload(toState, currentState)
      );
      const newState = updated?.stato_ciclo_vita || toState;
      setCurrentState(newState);
      setResult({ ok: true, message: `Stato aggiornato a ${STATE_LABELS[newState] || newState}.` });
      setPending(null);
      // Segnalato il 26/09 — lo stato qui si aggiornava correttamente,
      // ma chi contiene questo componente (il pannello di dettaglio, la
      // tabella sotto) non veniva mai avvisato: restava con il dato
      // vecchio finché non si ricaricava tutto a mano. onStateChanged è
      // facoltativo apposta — chi non lo passa continua a funzionare
      // come prima, senza rompersi.
      onStateChanged?.(newState);
    } catch (err) {
      if (err?.status === 409) {
        // Regola E.4 del collaudo: ricaricare, non limitarsi a un messaggio.
        setResult({ ok: false, message: 'Qualcosa ha già cambiato lo stato di questa risorsa nel frattempo. Stato ricaricato — verifica prima di riprovare.' });
        setPending(null);
        loadState();
      } else {
        setResult({ ok: false, message: err?.detail || 'Errore durante la transizione di stato.' });
      }
    } finally {
      setSending(false);
    }
  }

  if (!resourceId) return null;

  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px dashed rgba(0,0,0,0.15)', background: '#fff' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B6A65', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        Transizione di stato
        {KM_CONFIG.HR_CONSOLE_MOCK_MODE && (
          <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#FAEEDA', color: '#633806', textTransform: 'none', letterSpacing: 0 }}>
            DATI DI SVILUPPO — non demo
          </span>
        )}
      </div>

      {status === 'loading' && <div style={{ fontSize: 12, color: '#6B6A65' }}>Verifica stato in corso…</div>}

      {status === 'not_tracked' && (
        <div style={{ fontSize: 11.5, color: '#6B6A65' }}>
          Nessuna transizione disponibile: questa persona non è ancora un
          candidato tracciato in TalentGPT (situazione normale per la
          maggior parte del bacino MultiFitting oggi).
        </div>
      )}
      {status === 'error' && <div style={{ fontSize: 11.5, color: '#791F1F' }}>{errorMessage}</div>}

      {status === 'ready' && (
        <>
          {!currentState && (
            <div style={{ fontSize: 12, color: '#6B6A65', marginBottom: 8 }}>
              Ciclo di vita non ancora valorizzato per questa risorsa (campo introdotto di recente) — nessuna transizione proponibile finché non ha un valore.
            </div>
          )}

          {currentState && allowedTransitions(currentState).length === 0 && (
            <div style={{ fontSize: 12, color: '#6B6A65' }}>
              Nessuna transizione manuale disponibile da {STATE_LABELS[currentState] || currentState}.
            </div>
          )}

          {currentState && allowedTransitions(currentState).length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {allowedTransitions(currentState).map((toState) =>
                pending === toState ? (
                  <span key={toState} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12 }}>Confermi «{TRANSITION_LABELS[toState]}»?</span>
                    <button
                      type="button"
                      onClick={() => handleConfirm(toState)}
                      disabled={sending}
                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1 }}
                    >
                      {sending ? 'Invio…' : 'Sì, confermo'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPending(null)}
                      disabled={sending}
                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.18)', background: '#fff', cursor: sending ? 'default' : 'pointer' }}
                    >
                      Annulla
                    </button>
                  </span>
                ) : (
                  <button
                    key={toState}
                    type="button"
                    onClick={() => setPending(toState)}
                    style={{ fontSize: 12, padding: '5px 11px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.18)', background: '#fff', cursor: 'pointer' }}
                  >
                    {TRANSITION_LABELS[toState]}
                  </button>
                )
              )}
            </div>
          )}
        </>
      )}

      {result && (
        <div style={{ marginTop: 8, fontSize: 12, color: result.ok ? '#27500A' : '#791F1F' }}>
          {result.ok ? '✓ ' : '✗ '}{result.message}
        </div>
      )}
    </div>
  );
}
